/**
 * @fileoverview Reactivate User dialog.
 * @description Brings a deactivated user back through POST /users/{userId}/reactivate. The dialog
 * reads the user's details first, because the command carries their ETag as If-Match (a stale one,
 * 412 / 3022, re-reads the details and starts the form again). It asks for the role and reporting
 * manager the user comes back with, both prefilled from what they had when deactivated: the roles
 * are the Sales roles the admin may bring a user back as, and the managers are the ones the admin
 * may choose for the chosen role (the same choices Add User offers), cleared when the role changes
 * and prefilled once for the new role once its choices have settled (never replaced or blanked
 * after the admin has chosen, whatever refetches). It also asks for a reason from the
 * configuration's list, with a note for OTHER. The dialog says what the email will be: a
 * welcome-back email with a set-new-password link for someone who had activated, or a fresh
 * invitation for someone who never finished activating (the old password is never asked for
 * again). A "recent sign-in required" refusal (403 / 2009) offers Sign in again. The page mounts
 * this only while a user is being reactivated, so every open starts clean (see the modal-lifecycle
 * rule). The data and the command are in useReactivateUserDialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import ReactivateUserForm from '@/features/users/components/ReactivateUserForm'
import UserRequestError from '@/features/users/components/UserRequestError'
import {
  DEFAULT_REASON_NOTE_MAX_LENGTH,
  REACTIVATE_FORM_ID,
  REACTIVATE_TEST_ID,
} from '@/features/users/constants/lifecycle.constants'
import { useReactivateUserDialog } from '@/features/users/hooks/useReactivateUserDialog'
import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'
import Modal from '@/shared/components/molecules/Modal'

import type { ReactivatedUser } from '@/features/users/types/users.types'

interface ReactivateUserModalProps {
  userId: string
  onClose: () => void
  onReactivated: (result: ReactivatedUser) => void
}

/**
 * @component
 * @param {ReactivateUserModalProps} props - The user to reactivate, and the dialog's callbacks.
 * @param {string} props.userId - The user to reactivate.
 * @param {() => void} props.onClose - Dismisses the dialog without reactivating.
 * @param {(result: ReactivatedUser) => void} props.onReactivated - Called with the reactivated
 * user and how they came back after a successful reactivation, before the dialog closes.
 */
function ReactivateUserModal({ userId, onClose, onReactivated }: ReactivateUserModalProps) {
  const { t } = useTranslation()
  const dialog = useReactivateUserDialog({ userId, onClose, onReactivated })
  const { detail, configuration, roles } = dialog

  return (
    <Modal
      data-testid={`${REACTIVATE_TEST_ID}-modal`}
      open
      onClose={dialog.close}
      title={t('sales.userManagement.reactivateUser.title')}
      subtitle={
        detail
          ? t('sales.userManagement.reactivateUser.subtitle', {
              name: detail.user.displayName,
              id: detail.user.employeeCode ?? '',
            })
          : ''
      }
      size="lg"
      footer={
        <>
          <ModalFooterButton
            type="button"
            onClick={dialog.close}
            disabled={dialog.isSubmitting}
            data-testid={`${REACTIVATE_TEST_ID}-cancel-button`}
          >
            {t('sales.userManagement.editUserForm.cancel')}
          </ModalFooterButton>
          <ModalFooterButton
            type="submit"
            variant="primary"
            form={REACTIVATE_FORM_ID}
            data-testid={`${REACTIVATE_TEST_ID}-submit-button`}
            isLoading={dialog.isSubmitting}
            disabled={!dialog.canSubmit}
          >
            {t('sales.userManagement.reactivateUser.submit')}
          </ModalFooterButton>
        </>
      }
    >
      {dialog.loading && (
        <p className="text-[13px] text-slate" data-testid={`${REACTIVATE_TEST_ID}-loading`}>
          {t('sales.userManagement.viewUser.loading')}
        </p>
      )}

      {dialog.loadError && (
        <UserRequestError error={dialog.loadError} testIdPrefix={REACTIVATE_TEST_ID} />
      )}

      {detail && configuration && roles && (
        <div className="flex flex-col gap-4">
          {!dialog.allowed && (
            <p
              className="text-[13px] text-danger"
              data-testid={`${REACTIVATE_TEST_ID}-not-allowed`}
            >
              {t('sales.userManagement.lifecycle.notAvailable')}
            </p>
          )}
          {/* Not keyed by version: a re-read after a conflict must not wipe the typed reason. */}
          <ReactivateUserForm
            user={detail.user}
            roles={roles}
            reasons={configuration.lifecycleReasons.reactivation}
            noteMaxLength={
              configuration.limits.reasonNoteMaxLength || DEFAULT_REASON_NOTE_MAX_LENGTH
            }
            onSubmit={dialog.submit}
          />
          {dialog.submitError && (
            <UserRequestError error={dialog.submitError} testIdPrefix={REACTIVATE_TEST_ID} />
          )}
        </div>
      )}
    </Modal>
  )
}

export default ReactivateUserModal
